import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router';
import { PRODUCT_NAME, type PermissionKey } from '@ismtur/core';
import { useSession } from './stores/session';

declare module 'vue-router' {
  interface RouteMeta {
    /** Reachable without signing in. */
    public?: boolean;
    /** Only for visitors who are not signed in (the login page). */
    guestOnly?: boolean;
    /** Any one of these opens the page. */
    anyOf?: PermissionKey[];
    platform?: boolean;
    title?: string;
    /** Uses the full width of the window instead of the reading width. */
    wide?: boolean;
  }
}

export const router = createRouter({
  history: createWebHistory(),
  scrollBehavior: () => ({ top: 0 }),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('./views/LoginView.vue'),
      meta: { public: true, guestOnly: true, title: 'Giriş' },
    },
    {
      path: '/p/:token',
      name: 'proposal-public',
      component: () => import('./views/ProposalPublicView.vue'),
      meta: { public: true, title: 'Teklif' },
    },
    {
      path: '/pk/:token',
      name: 'package-public',
      component: () => import('./views/PackagePublicView.vue'),
      meta: { public: true, title: 'Paket' },
    },
    {
      path: '/invite/:token',
      name: 'invite',
      component: () => import('./views/InviteView.vue'),
      meta: { public: true, title: 'Davet' },
    },
    {
      path: '/',
      component: () => import('./components/AppShell.vue'),
      children: [
        {
          path: '',
          name: 'overview',
          component: () => import('./views/OverviewView.vue'),
          meta: { title: 'Pano' },
        },
        {
          path: 'sales',
          name: 'sales',
          component: () => import('./views/SalesView.vue'),
          meta: { title: 'Satışlar', anyOf: ['sales.view'] },
        },
        {
          path: 'sales/new',
          name: 'sale-new',
          component: () => import('./views/SaleNewView.vue'),
          meta: { title: 'Yeni satış', anyOf: ['sales.create'] },
        },
        {
          path: 'sales/:id',
          name: 'sale',
          component: () => import('./views/SaleDetailView.vue'),
          meta: { title: 'Satış', anyOf: ['sales.view'] },
        },
        {
          path: 'accounts',
          name: 'accounts',
          component: () => import('./views/AccountsView.vue'),
          meta: { title: 'Firmalar', anyOf: ['customers.view'] },
        },
        {
          path: 'accounts/:id',
          name: 'account',
          component: () => import('./views/AccountDetailView.vue'),
          meta: { title: 'Firma', anyOf: ['customers.view'] },
        },
        {
          path: 'suppliers',
          name: 'suppliers',
          component: () => import('./views/SuppliersView.vue'),
          meta: { title: 'Tedarikçiler', anyOf: ['suppliers.view', 'suppliers.manage'] },
        },
        {
          path: 'suppliers/:id',
          name: 'supplier',
          component: () => import('./views/SupplierDetailView.vue'),
          meta: { title: 'Tedarikçi', anyOf: ['suppliers.view', 'suppliers.manage'] },
        },
        {
          path: 'contacts',
          name: 'contacts',
          component: () => import('./views/ContactsView.vue'),
          meta: { title: 'Kişiler', anyOf: ['customers.view'] },
        },
        {
          path: 'contacts/:id',
          name: 'contact',
          component: () => import('./views/ContactDetailView.vue'),
          meta: { title: 'Kişi', anyOf: ['customers.view'] },
        },
        {
          path: 'members',
          name: 'members',
          component: () => import('./views/MembersView.vue'),
          meta: { title: 'Ekip', anyOf: ['members.view'] },
        },
        {
          path: 'roles',
          name: 'roles',
          component: () => import('./views/RolesView.vue'),
          meta: { title: 'Roller ve yetkiler', anyOf: ['roles.manage', 'members.view'] },
        },
        {
          path: 'departments',
          name: 'departments',
          component: () => import('./views/DepartmentsView.vue'),
          meta: { title: 'Birimler' },
        },
        {
          path: 'companies',
          name: 'companies',
          component: () => import('./views/CompaniesView.vue'),
          meta: { title: 'Şirketler' },
        },
        {
          path: 'invitations',
          name: 'invitations',
          component: () => import('./views/InvitationsView.vue'),
          meta: { title: 'Davetler', anyOf: ['invitations.manage'] },
        },
        {
          path: 'audit',
          name: 'audit',
          component: () => import('./views/AuditView.vue'),
          meta: { title: 'Denetim kaydı', anyOf: ['audit.view'] },
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import('./views/SettingsView.vue'),
          meta: { title: 'Acente ayarları', anyOf: ['tenant.settings'] },
        },
        {
          path: 'profile',
          name: 'profile',
          component: () => import('./views/ProfileView.vue'),
          meta: { title: 'Profilim' },
        },
        {
          path: 'platform',
          name: 'platform',
          component: () => import('./views/PlatformView.vue'),
          meta: { title: 'Acenteler', platform: true },
        },
        {
          path: 'platform/tenants/:id',
          name: 'platform-tenant',
          component: () => import('./views/PlatformTenantView.vue'),
          meta: { title: 'Acente', platform: true },
        },
        {
          path: 'reports',
          name: 'reports',
          component: () => import('./views/ReportsView.vue'),
          meta: { title: 'Raporlar', anyOf: ['reports.view'] },
        },
        {
          path: 'billing',
          name: 'billing',
          component: () => import('./views/BillingView.vue'),
          meta: { title: 'Abonelik', anyOf: ['billing.view'] },
        },
        {
          path: 'calendar',
          name: 'calendar',
          component: () => import('./views/CalendarView.vue'),
          meta: { title: 'Takvim', anyOf: ['sales.view'], wide: true },
        },
        {
          path: 'options',
          name: 'options',
          component: () => import('./views/OptionsView.vue'),
          meta: { title: 'Opsiyonlar', anyOf: ['options.view'] },
        },
        {
          path: 'checkins',
          name: 'checkins',
          component: () => import('./views/CheckinsView.vue'),
          meta: { title: 'Check-in', anyOf: ['sales.view'] },
        },
        {
          path: 'packages',
          name: 'packages',
          component: () => import('./views/PackagesView.vue'),
          meta: { title: 'Paket teklifler', anyOf: ['tour_packages.manage'] },
        },
        {
          path: 'packages/:id',
          name: 'package',
          component: () => import('./views/PackageDetailView.vue'),
          meta: { title: 'Paket', anyOf: ['tour_packages.manage'] },
        },
        {
          path: 'payables',
          name: 'payables',
          component: () => import('./views/PayablesView.vue'),
          meta: { title: 'Bekleyen ödemeler', anyOf: ['suppliers.view'] },
        },
        {
          path: 'payments',
          name: 'payments',
          component: () => import('./views/PaymentsView.vue'),
          meta: { title: 'Ödemeler', anyOf: ['payments.view', 'suppliers.view', 'cards.manage'] },
        },
        {
          path: 'proposals',
          name: 'proposals',
          component: () => import('./views/ProposalsView.vue'),
          meta: { title: 'Teklifler', anyOf: ['proposals.view'] },
        },
        {
          path: 'proposals/new',
          name: 'proposal-new',
          component: () => import('./views/ProposalNewView.vue'),
          meta: { title: 'Yeni teklif', anyOf: ['proposals.create'] },
        },
        {
          path: 'proposals/:id',
          name: 'proposal',
          component: () => import('./views/ProposalDetailView.vue'),
          meta: { title: 'Teklif', anyOf: ['proposals.view'] },
        },
        {
          path: 'tasks',
          name: 'tasks',
          component: () => import('./views/TasksView.vue'),
          meta: { title: 'Görevler', anyOf: ['tasks.view'] },
        },
        {
          path: ':module(visa)',
          name: 'module',
          component: () => import('./views/ModuleView.vue'),
        },
      ],
    },
    {
      path: '/:rest(.*)*',
      name: 'not-found',
      component: () => import('./views/NotFoundView.vue'),
      meta: { public: true, title: 'Sayfa bulunamadı' },
    },
  ],
});

function allowed(to: RouteLocationNormalized): boolean {
  const session = useSession();
  if (to.meta.platform) return Boolean(session.user?.isPlatformAdmin);
  if (!to.meta.anyOf) return true;
  return to.meta.anyOf.some((key) => session.can(key));
}

router.beforeEach(async (to) => {
  const session = useSession();
  if (!session.loaded) await session.load();

  if (to.meta.guestOnly && session.user) return { name: 'overview' };
  if (!to.meta.public && !session.user) {
    return { name: 'login', query: to.fullPath === '/' ? {} : { next: to.fullPath } };
  }
  if (!to.meta.public && !allowed(to)) return { name: 'overview' };
  return true;
});

// After a release, a tab opened earlier asks for screens by their old file names,
// which are gone. Load the new version into the page being opened instead of
// standing still; at most once in 10 s, so a real outage cannot loop.
const STALE = /dynamically imported module|Importing a module script failed|Unable to preload CSS/i;
router.onError((error: unknown, to) => {
  if (!(error instanceof Error) || !STALE.test(error.message)) return;
  try {
    const last = Number(sessionStorage.getItem('ismtur.reloaded-at') ?? 0);
    if (Date.now() - last < 10_000) return;
    sessionStorage.setItem('ismtur.reloaded-at', String(Date.now()));
  } catch {
    /* storage unavailable: reload anyway */
  }
  window.location.assign(to.fullPath);
});

router.afterEach((to) => {
  const session = useSession();
  const tenant = session.active?.tenant.name;
  document.title = [to.meta.title, tenant ?? PRODUCT_NAME].filter(Boolean).join(' · ');
});
